// utilies/imagery/LoadAerialImageryAsLayer.js
import * as Cesium from "cesium";
import Type from "../Types";
import { createUniqueId, insertByPriority, showToast } from "../Utility";

const AERIAL_TYPE = Type?.ASSET?.IMAGERY;

function isNumericId(v) {
  return typeof v === "number" || /^[0-9]+$/.test(String(v));
}

/* -------------------- Provider factory (same behavior as GeoTiff loader) -------------------- */

function normalizeTmsUrl(rawUrl) {
  if (rawUrl == null) return rawUrl;
  let u = String(rawUrl).trim();
  u = u.replace(/\/tilemapresource\.xml$/i, "/");
  if (!u.endsWith("/")) u += "/";
  return u;
}

async function tryLoadFromTileJson(baseUrl) {
  const tileJsonUrl = `${baseUrl}tiles.json`;
  const res = await fetch(tileJsonUrl, { method: "GET" });
  if (!res.ok) return null;

  const tj = await res.json();
  const tiles = Array.isArray(tj?.tiles) ? tj.tiles : [];
  const template = tiles[0];
  const bounds = Array.isArray(tj?.bounds) ? tj.bounds : null;

  if (!template || !bounds || bounds.length !== 4) return null;

  let urlTemplate = `${baseUrl}${String(template).replace(/^\/+/, "")}`;

  const scheme = String(tj?.scheme || "").toLowerCase();
  if (scheme === "tms") {
    urlTemplate = urlTemplate.replace(/{y}/g, "{reverseY}");
  }

  const w = Number(bounds[0]);
  const s = Number(bounds[1]);
  const e = Number(bounds[2]);
  const n = Number(bounds[3]);
  if (![w, s, e, n].every(Number.isFinite)) return null;

  const rectangle = Cesium.Rectangle.fromDegrees(w, s, e, n);
  const minzoom = Number.isFinite(tj?.minzoom) ? tj.minzoom : undefined;
  const maxzoom = Number.isFinite(tj?.maxzoom) ? tj.maxzoom : undefined;

  const p = new Cesium.UrlTemplateImageryProvider({
    url: urlTemplate,
    tilingScheme: new Cesium.WebMercatorTilingScheme(),
    rectangle,
    minimumLevel: minzoom,
    maximumLevel: maxzoom,
  });

  p.enablePickFeatures = false;
  return p;
}

async function createProviderFromIdOrUrl(idOrUrl) {
  const isNum = isNumericId(idOrUrl);

  if (isNum) {
    const assetId = typeof idOrUrl === "number" ? idOrUrl : parseInt(String(idOrUrl), 10);
    return await Cesium.IonImageryProvider.fromAssetId(assetId);
  }

  const baseUrl = normalizeTmsUrl(idOrUrl);

  // Prefer tiles.json (TileJSON)
  try {
    const p = await tryLoadFromTileJson(baseUrl);
    if (p) return p;
  } catch (_) {
    // ignore, fall back to TMS
  }

  // Fall back to TMS (tilemapresource.xml)
  return await Cesium.TileMapServiceImageryProvider.fromUrl(baseUrl);
}

/* -------------------- Crash guard: prevent "Expected rectangle ... undefined" -------------------- */

function isValidRectangle(r) {
  if (!r || typeof r !== "object") return false;
  const w = r.west,
    s = r.south,
    e = r.east,
    n = r.north;
  return [w, s, e, n].every((v) => typeof v === "number" && Number.isFinite(v));
}

function ensureProviderRectangleOrLog(provider, onError) {
  const rect = provider?.rectangle ?? provider?.tilingScheme?.rectangle;

  if (!isValidRectangle(rect)) {
    const err = new Error("ImageryProvider rectangle is undefined/invalid. Layer will NOT be added to prevent Cesium render crash.");
    try {
      console.error(err);
    } catch (_) {}
    try {
      onError?.(err);
    } catch (_) {}
    return false;
  }

  if (!provider.rectangle) {
    try {
      provider.rectangle = rect;
    } catch (_) {}
  }

  return true;
}

/* -------------------- main: add AERIAL imagery as layer (mapObjects + viewer.imageryLayers) -------------------- */

export async function loadAerialImageryTypeAsync({
  viewerRef,
  setMapObjects,

  // source
  idOrUrl,

  // layer identity
  id: passedId = null,
  fileName = "Imagery",

  // styling/state
  savedStyles = null,
  projectId = null,
  serverLayerId = null,
  isUrl = false,
  url = "",
  priority = 0,
  isDisableLoading = false,

  // error handling (same idea as GeoTiff loader)
  maxTileErrors = 5,
  replaceExisting, // if undefined -> numeric => true, url => false
  onError = () => {},

  projectTypeLoading = "main",
} = {}) {
  const logOnly = (e) => {
    try {
      console.error(e);
    } catch (_) {}
    try {
      onError(e);
    } catch (_) {}
  };

  const viewer = viewerRef?.current;
  if (!viewer?.imageryLayers) {
    logOnly(new Error("Invalid Cesium viewer"));
    return null;
  }

  const layers = viewer.imageryLayers;

  const isUpdateExisting = !!passedId;
  const id = passedId || createUniqueId();

  const styleInvisible = savedStyles?.style_invisible_default === true;
  const initialVisible = isDisableLoading ? false : !styleInvisible;

  const isNum = isNumericId(idOrUrl);
  const shouldReplaceExisting = typeof replaceExisting === "boolean" ? replaceExisting : isNum;
  const isOverlayMode = !shouldReplaceExisting;

  const baseMapObject = {
    id,
    name: "Imagery",
    type: AERIAL_TYPE,
    file_name: fileName,
    file_type: null,
    visible: initialVisible,
    loading: initialVisible,
    is_edited: false,
    layer_id: serverLayerId ?? null,
    project_id: projectId ?? null,
    url: url ? url : isUrl ? String(idOrUrl ?? "") : "",
    imagery: {
      source: idOrUrl,
      alpha: savedStyles?.style_alpha ?? 1,
      maxTileErrors,
      replaceExisting: shouldReplaceExisting,
    },
    imageryLayer: null,
    style_invisible_default: !!styleInvisible,
    priority: Number.isFinite(Number(priority)) ? Number(priority) : 0,
    _priorityOrder: typeof performance !== "undefined" ? performance.now() : Date.now(),
  };

  // placeholder insert/update
  if (projectTypeLoading === "main" && typeof setMapObjects === "function") {
    if (!isUpdateExisting) {
      setMapObjects((prev) => insertByPriority(prev, baseMapObject));
    } else {
      setMapObjects((prev) => {
        const next = [...prev];
        const idx = next.findIndex((o) => o.id === id);
        if (idx === -1) return next;

        const current = next[idx];
        if (current?.imageryLayer) {
          try {
            layers.remove(current.imageryLayer, true);
          } catch (_) {}
        }

        next[idx] = { ...current, ...baseMapObject, imageryLayer: null };
        return next;
      });
    }
  }

  // invisible-by-style => no load now
  if (!initialVisible) {
    if (projectTypeLoading === "main" && typeof setMapObjects === "function") {
      setMapObjects((prev) => prev.map((o) => (o.id === id ? { ...o, loading: false, imageryLayer: null } : o)));
    }
    return { id, imageryLayer: null };
  }

  // keep previous layers for restore (replace mode)
  const previousLayers = [];
  for (let i = layers.length - 1; i >= 0; i--) previousLayers.push(layers.get(i));
  let previousRemoved = false;

  let provider;
  try {
    provider = await createProviderFromIdOrUrl(idOrUrl);
    if (provider?.readyPromise) await provider.readyPromise;

    // Critical: prevent Cesium render crash if rectangle is missing/invalid.
    if (!ensureProviderRectangleOrLog(provider, onError)) {
      // keep placeholder but mark not loading
      if (projectTypeLoading === "main" && typeof setMapObjects === "function") {
        setMapObjects((prev) => prev.map((o) => (o.id === id ? { ...o, loading: false, imageryLayer: null } : o)));
      }
      return null;
    }
  } catch (e) {
    logOnly(e);
    if (projectTypeLoading === "main" && typeof setMapObjects === "function") {
      setMapObjects((prev) => prev.map((o) => (o.id === id ? { ...o, loading: false, imageryLayer: null } : o)));
    }
    try {
      showToast(e?.message || "Failed to load imagery provider", Type.NOTIFICATION.ERROR);
    } catch (_) {}
    return null;
  }

  // overlay: remove previous aerial overlays (avoid stacking)
  if (isOverlayMode) {
    for (let i = layers.length - 1; i >= 0; i--) {
      const l = layers.get(i);
      if (l && l.__atlasAerialOverlay === true) {
        try {
          layers.remove(l, true);
        } catch (_) {}
      }
    }
  }

  let imageryLayer;
  try {
    imageryLayer = layers.addImageryProvider(provider);
    imageryLayer.__atlasAerialOverlay = true;

    try {
      imageryLayer.alpha = Number.isFinite(Number(savedStyles?.style_alpha)) ? Number(savedStyles.style_alpha) : 1;
    } catch (_) {}
    try {
      imageryLayer.show = true;
    } catch (_) {}
  } catch (e) {
    logOnly(e);
    if (projectTypeLoading === "main" && typeof setMapObjects === "function") {
      setMapObjects((prev) => prev.map((o) => (o.id === id ? { ...o, loading: false, imageryLayer: null } : o)));
    }
    try {
      showToast(e?.message || "Failed to attach imagery layer", Type.NOTIFICATION.ERROR);
    } catch (_) {}
    return null;
  }

  let tileErrorCount = 0;
  let disposed = false;

  const restorePrevious = () => {
    try {
      layers.remove(imageryLayer, true);
    } catch (_) {}

    if (previousRemoved) {
      for (let i = previousLayers.length - 1; i >= 0; i--) {
        try {
          layers.add(previousLayers[i]);
        } catch (_) {}
      }
      previousRemoved = false;
    }
  };

  const onProviderError = (err) => {
    try {
      if (err) err.retry = false;
    } catch (_) {}

    const statusCode = err?.statusCode;
    const message = String(err?.message || "");
    const isMissingTile = statusCode === 403 || statusCode === 404 || /\b(403|404)\b/.test(message);

    if (isMissingTile) {
      logOnly(err);
      return;
    }

    if (isOverlayMode) {
      logOnly(err);
      return;
    }

    tileErrorCount += 1;
    logOnly(err);

    if (tileErrorCount >= maxTileErrors) {
      restorePrevious();
      dispose();
    }
  };

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    try {
      provider?.errorEvent?.removeEventListener(onProviderError);
    } catch (_) {}
  };

  try {
    provider?.errorEvent?.addEventListener(onProviderError);
  } catch (_) {}

  if (shouldReplaceExisting) {
    setTimeout(() => {
      if (disposed) return;
      if (tileErrorCount === 0) {
        try {
          for (const l of previousLayers) layers.remove(l, false);
          previousRemoved = true;
        } catch (_) {}
      }
    }, 2000);
  }

  if (projectTypeLoading === "main" && typeof setMapObjects === "function") {
    setMapObjects((prev) =>
      prev.map((o) =>
        o.id === id
          ? {
              ...o,
              imageryLayer,
              loading: false,
              visible: true,
            }
          : o,
      ),
    );
  }

  try {
    imageryLayer.__aerialDispose = dispose;
  } catch (_) {}

  return { id, imageryLayer };
}

/**
 * Toggle visibility for AERIAL IMAGERY mapObject (lazy-load if not loaded).
 */
export async function setAerialImageryVisible({ viewerRef, setMapObjects, layerId, visible, onError = () => {} }) {
  const viewer = viewerRef?.current;
  if (!viewer) return;

  let snapshot = null;

  if (typeof setMapObjects === "function") {
    setMapObjects((prev) =>
      prev.map((o) => {
        if (o.id !== layerId) return o;
        snapshot = o;
        return { ...o, visible: !!visible };
      }),
    );
  }

  if (!snapshot) return;

  if (snapshot.imageryLayer) {
    try {
      snapshot.imageryLayer.show = !!visible;
    } catch (_) {}
    return;
  }

  if (!visible) return;

  try {
    await loadAerialImageryTypeAsync({
      viewerRef,
      setMapObjects,
      idOrUrl: snapshot?.imagery?.source ?? snapshot?.url,
      id: snapshot.id,
      fileName: snapshot.file_name || "Aerial Imagery",
      savedStyles: {
        style_alpha: snapshot?.imagery?.alpha ?? 1,
        style_invisible_default: false,
      },
      projectId: snapshot.project_id ?? null,
      serverLayerId: snapshot.layer_id ?? null,
      isUrl: false,
      url: snapshot.url ?? "",
      priority: snapshot.priority ?? 0,
      isDisableLoading: false,
      maxTileErrors: snapshot?.imagery?.maxTileErrors ?? 5,
      replaceExisting: snapshot?.imagery?.replaceExisting,
      onError,
      projectTypeLoading: "main",
    });
  } catch (e) {
    try {
      console.error(e);
    } catch (_) {}
    onError(e);
  }
}

/**
 * Remove AERIAL IMAGERY layer completely.
 */
export function removeAerialImageryLayer({ viewerRef, setMapObjects, layerId }) {
  const viewer = viewerRef?.current;
  if (!viewer?.imageryLayers) return;

  const layers = viewer.imageryLayers;

  if (typeof setMapObjects !== "function") return;

  setMapObjects((prev) => {
    const obj = prev.find((o) => o.id === layerId);
    if (obj?.imageryLayer) {
      try {
        obj.imageryLayer.__aerialDispose?.();
      } catch (_) {}
      try {
        layers.remove(obj.imageryLayer, true);
      } catch (_) {}
    }
    return prev.filter((o) => o.id !== layerId);
  });
}
